/**
 * Validation for organisation create/edit endpoints. Mirrors the
 * org schema in worker-api/data/schema.ts.
 */

import { z } from 'zod';

import { emailSchema, tzSchema } from './primitives.js';

export const organizationCreateInputSchema = z.object({
  name: z.string().min(1).max(200),
  type: z.enum(['client', 'internal']),
  timezone: tzSchema.default('Australia/Sydney'),
  primaryContactName: z.string().max(200).optional(),
  primaryContactEmail: emailSchema.optional(),
  primaryContactPhone: z.string().max(50).optional(),
  /** Optional: domains used to route inbound email. Each domain must
   *  be globally unique — the route surfaces 409 on collision. */
  domains: z.array(z.string().min(3).max(253)).max(20).default([]),
});
export type OrganizationCreateInput = z.infer<
  typeof organizationCreateInputSchema
>;

/**
 * Organisation update — every field is optional. The route applies
 * only fields that are present (PATCH semantics). Type and id never
 * change post-create.
 */
export const organizationUpdateInputSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  timezone: tzSchema.optional(),
  primaryContactName: z.string().max(200).nullable().optional(),
  primaryContactEmail: emailSchema.nullable().optional(),
  primaryContactPhone: z.string().max(50).nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
});
export type OrganizationUpdateInput = z.infer<
  typeof organizationUpdateInputSchema
>;

/** A single domain to attach to an existing organisation. */
export const organizationDomainAddInputSchema = z.object({
  domain: z.string().min(3).max(253),
});
export type OrganizationDomainAddInput = z.infer<
  typeof organizationDomainAddInputSchema
>;
